import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Box } from '@material-ui/core';
import { LinearProgressOverlay } from '@campfire/linear-progress-overlay';
import { StringParam, useQueryParam } from 'use-query-params';
import { DateTime } from 'luxon';
import {
  COLLAPSED_VIEW_COLUMN_NO,
  EXPANDED_VIEW_COLUMN_NO,
  RosterHeader,
  useSharedSessionResults,
} from './RosterHeader';
import { RosterActionsFooter } from './RosterActionsFooter';
import { CurrentRosterState, RowAction, UpdatedRostering, Volunteer } from './roster-types';
import { GetRosterScreenRosterData_vm_activity_sessions as Session } from './__generated__/GetRosterScreenRosterData';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { RosterTable, RosterTableForPrint } from './roster-table/RosterTable';
import { useSharedPromptState } from './roster-table/RosterTableSharedState';
import { getRosterAction, getRosteringSessionStatus, getSessionStatuses } from './roster-functions';
import { MaxVolunteersPrompt } from './roster-table/MaxVolunteersPrompt';
import { RosterNotesDialog } from '../../common/RosterNotesDialog';
import { RosterScreenDraftRoster_sessionNotes as SesionNoteType } from './__generated__/RosterScreenDraftRoster';
import { ConflictingDialog } from '../../bulk-roster/ConflictingDialog';
import { ConflictingRosterType, useGetConflictingRosters } from '../../useGetConflictingRoster';
import { filterSessionsByActivityDate } from './one-off-session-utils';
import { useDeleteOneOffSession, useEditOneOffSession } from '../../common/add-one-off-session-api';
import { EditOneOffSessionDialog } from '../../common/EditOneOffSessionDialog';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { ConfirmationDialog } from '../../../../../common/dialogs/ConfirmationDialog';
import { ApplicantHold } from '../../../../../common/hooks/useApplicantHolds';

export const ACTIVITY_TEAM_ROSTER = '215px';

type SettingRoster = {
  volunteerId: string;
  sessionId: string;
  roster: any;
};

interface Props {
  smsConfirmationRefetch: () => void;
  loading: boolean;
  sessions: Session[];
  draftRoster?: Volunteer[];
  updateRostering: (volunteerId: string, sessionId: string, action: RowAction) => void;
  updatedRosterings: UpdatedRostering[];
  currentRosterState: CurrentRosterState;
  hasPublishedRoster: boolean;
  handlePublishRoster: (x: boolean) => void;
  handleDiscardChanges: () => void;
  setOpenAddVolunteersDialog: (x: boolean) => void;
  isRosterCancelled: boolean;
  isRosterCompleted: boolean;
  onSubmitReport: any;
  publishedRoster: any;
  activityDate: string;
  activityName: string;
  activityStatus: string;
  activityId: string;
  onChangeAvailability: (volunteerId: string) => void;
  setShowCancelActivityDialog: (x: boolean) => void;
  handleSuccessRefetch?: () => void;
  rosterNotes?: string | null;
  rosterName?: string | null;
  onSaveRosterNotes: (rosterNotes: string | null, rosterName?: string | null) => void;
  sessionNotes: SesionNoteType[];
  onChangeSessionNote: (sessionId: string, notes: string) => void;
  componentRef: React.RefObject<HTMLDivElement>;
  orgLogo?: string | null;
  isPendingApproval?: boolean;
  canApproveRoster?: boolean;
  onApproveRosterClick?: () => void;
  onRejectRosterClick?: () => void;
  onToggleRestricted?: (isRestricted: boolean) => void;
  allowIndividualShiftTimes?: boolean;
  onSetSessionTime?: (volunteerId: string, sessionId: string, startTime: string | null, endTime: string | null) => void;
  onRemoveRostering?: (volunteerId: string, sessionId: string) => void;
  applicantHolds?: ApplicantHold[];
}

export const Roster = (props: Props) => {
  const {
    sessionNotes,
    onChangeSessionNote,
    loading,
    setOpenAddVolunteersDialog,
    sessions,
    draftRoster,
    updateRostering,
    updatedRosterings,
    currentRosterState,
    hasPublishedRoster,
    handlePublishRoster,
    handleDiscardChanges,
    isRosterCancelled,
    isRosterCompleted,
    onSubmitReport,
    publishedRoster,
    activityDate,
    activityName,
    activityStatus,
    activityId,
    onChangeAvailability,
    setShowCancelActivityDialog,
    handleSuccessRefetch,
    rosterNotes,
    rosterName,
    onSaveRosterNotes,
    componentRef,
    orgLogo,
    isPendingApproval,
    canApproveRoster,
    onApproveRosterClick,
    onRejectRosterClick,
    onToggleRestricted,
    allowIndividualShiftTimes,
    onSetSessionTime,
    onRemoveRostering,
    applicantHolds,
  } = props;
  const [sortVolsRowBy, setSortVolsRowBy] = useQueryParam('sortVolsRowBy', StringParam);
  const [sortedVolunteerIdsByEnrolmentDate, setSortedVolunteerIdsByEnrolmentDate] = React.useState<
    string[] | undefined
  >();
  const selectedRoster = useRef<Volunteer>();
  const { theme } = useCampfireTheme();
  const sessionStatuses = getSessionStatuses(theme);
  const [showRosterNotes, setShowRosterNotes] = React.useState(false);
  const [isEditRosterNote, setIsEditRosterNote] = React.useState(false);
  const [editingSession, setEditingSession] = React.useState<any>(null);
  const [showEditDialog, setShowEditDialog] = React.useState(false);
  const [settingRoster, setSettingRoster] = React.useState<SettingRoster | null>(null);
  const [deletingSession, setDeletingSession] = React.useState<any>(null);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = React.useState(false);

  // One-off session API hooks
  const deleteOneOffSession = useDeleteOneOffSession();
  const editOneOffSession = useEditOneOffSession();
  const { setSnackbar } = useSnackbar();

  const {
    sessionResultsStart,
    sessionResultsEnd,
    sessionPage,
    setSessionPage,
    columnSize,
    setColumnSize,
    isExpanded,
    totalSessionPages,
    setTotalSessionPages,
  } = useSharedSessionResults();

  // Filter sessions to include regular sessions + one-off sessions for this specific date
  const filteredSessions = filterSessionsByActivityDate(sessions || [], activityDate);

  const sessionsData = filteredSessions?.map((s) => {
    if (draftRoster && s.maxVolunteers) {
      const attending: any[] = draftRoster.flatMap((dr) => {
        const attendingLower = dr.publishedSessionIds.filter((ps) => {
          return ps === s.sessionId;
        });
        return attendingLower;
      });
      return {
        ...s,
        isMaxed: attending.length >= s.maxVolunteers,
      };
    }
    return s;
  });
  const [viewMaxVolunteersPrompt, setViewMaxVolunteersPrompt] = useState<boolean>(false);
  const fetchConflictRoster = useGetConflictingRosters();
  const [conflictingRoster, setConflictingRoster] = React.useState<ConflictingRosterType>([]);
  const getConflictRoster = (volunteerId: string, sessionId: string) => {
    const session = sessionsData.find((s: any) => s?.sessionId === sessionId);
    if (!session || !session.startTime || !session.endTime) {
      return Promise.resolve({ conflictingRosters: [] });
    }

    return fetchConflictRoster
      .run({
        volunteerId,
        activityDate,
        startTime: session?.startTime,
        endTime: session?.endTime,
      })
      .then((response) => response?.data?.data);
  };
  useEffect(() => {
    if (isExpanded) {
      setColumnSize(EXPANDED_VIEW_COLUMN_NO);
      return;
    }
    if (columnSize !== COLLAPSED_VIEW_COLUMN_NO) {
      setColumnSize(COLLAPSED_VIEW_COLUMN_NO);
    }
  }, [isExpanded]);

  useEffect(() => setTotalSessionPages(Math.ceil(sessionsData.length / columnSize)), [sessionsData, columnSize]);

  useEffect(() => {
    if (totalSessionPages && sessionPage > totalSessionPages) {
      setSessionPage(sessionPage - 1);
    }
  }, [totalSessionPages]);

  const slicedSessions = useMemo(() => {
    return sessionsData.slice(sessionResultsStart, sessionResultsEnd);
  }, [sessionsData, sessionResultsStart, sessionResultsEnd]);

  const {
    promptVolunteerId,
    promptSessionId,
    setPromptVolunteerId,
    setPromptSessionId,
    hidePromptForDate,
    setHidePromptForDate,
  } = useSharedPromptState();

  useEffect(() => {
    if (activityId && !hasPublishedRoster) {
      setSortVolsRowBy('availability');
    }
  }, [hasPublishedRoster, activityId]);

  const showMaxVolunteersPrompt = (showPrompt: boolean, volunteerId: string, sessionId: string) => {
    setViewMaxVolunteersPrompt(showPrompt);
    setPromptVolunteerId(volunteerId);
    setPromptSessionId(sessionId);
  };

  const checkPromptSessionStatus = (sessionStatus: string) => {
    if (sessionStatus === 'Rostered') {
      return false;
    }
    if (sessionStatus === 'To be rostered') {
      return false;
    }
    return true;
  };

  const handleFillRoster = () => {
    if (!draftRoster) return;

    const assignedVolunteerIds = new Set<string>();

    draftRoster.forEach((r) => {
      if (r.draftedSessionIds?.length > 0) {
        assignedVolunteerIds.add(r.volunteerId);
      }
    });

    sessionsData.forEach((session) => {
      const minVols = session.minVolunteers ?? 0;
      if (minVols === 0) return;

      const currentlyDrafted = draftRoster.filter((r) => r.draftedSessionIds?.includes(session.sessionId)).length;
      let slotsToFill = minVols - currentlyDrafted;
      if (slotsToFill <= 0) return;

      const available: Volunteer[] = [];
      const notIndicated: Volunteer[] = [];

      draftRoster.forEach((r) => {
        if (assignedVolunteerIds.has(r.volunteerId)) return;
        if (r.unavailable) return;
        if (r.attending === false) return;
        const status = getRosteringSessionStatus(session.sessionId, r, sessionStatuses).text;
        if (status === 'Available') available.push(r);
        else if (status === 'No indication') notIndicated.push(r);
      });

      for (let i = notIndicated.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [notIndicated[i], notIndicated[j]] = [notIndicated[j], notIndicated[i]];
      }

      const candidates = [...available, ...notIndicated];

      candidates.forEach((vol) => {
        if (slotsToFill <= 0) return;
        updateRostering(vol.volunteerId, session.sessionId, 'add');
        assignedVolunteerIds.add(vol.volunteerId);
        slotsToFill--;
      });
    });
  };

  const onClickRosterStatus = (session: any, roster: any) => {
    selectedRoster.current = roster;
    if (
      session.isMaxed &&
      !hidePromptForDate &&
      checkPromptSessionStatus(getRosteringSessionStatus(session.sessionId, roster, sessionStatuses).text)
    ) {
      showMaxVolunteersPrompt(true, roster.volunteerId, session.sessionId);
      return;
    }
    const action = getRosterAction(getRosteringSessionStatus(session.sessionId, roster, sessionStatuses).text);

    // Always update immediately (optimistic update)
    updateRostering(roster.volunteerId, session.sessionId, action);

    // For 'add' actions on non-published rosterings, check for conflicts asynchronously
    if (action === 'add') {
      const wasOriginallyRostered = roster.publishedSessionIds?.includes(session.sessionId);

      if (!wasOriginallyRostered) {
        getConflictRoster(roster.volunteerId, session.sessionId).then((res) => {
          if (!res) return;
          if (res.conflictingRosters.length > 0) {
            setConflictingRoster(res.conflictingRosters);
            setSettingRoster({
              sessionId: session.sessionId,
              volunteerId: roster.volunteerId,
              roster,
            });
          }
        });
      }
    }
  };

  const onCancelConflict = () => {
    // Reverse the optimistic update by removing the rostering
    if (settingRoster) {
      updateRostering(settingRoster.volunteerId, settingRoster.sessionId, 'remove');
    }
    setSettingRoster(null);
  };

  const onContinueConflict = () => {
    // The update was already done optimistically, just close the dialog
    setSettingRoster(null);
  };

  const onTick = (tick: boolean) => setHidePromptForDate(tick);
  const onOk = () => {
    if (selectedRoster.current) {
      updateRostering(
        promptVolunteerId,
        promptSessionId,
        getRosterAction(getRosteringSessionStatus(promptSessionId, selectedRoster.current, sessionStatuses).text)
      );
    }
    setViewMaxVolunteersPrompt(false);
  };

  const onShowRosterNotes = (type: string) => {
    setIsEditRosterNote(type === 'edit');
    setShowRosterNotes(true);
  };

  const handleSaveRosterNotes = (newRosterNotes: string | null, newRosterName?: string | null) => {
    onSaveRosterNotes(newRosterNotes, newRosterName);
    setShowRosterNotes(false);
  };

  // One-off session handlers
  const handleEditOneOffSession = (sessionId: string) => {
    const session = sessions.find((s) => s.sessionId === sessionId);
    if (session) {
      setEditingSession(session);
      setShowEditDialog(true);
    }
  };

  const handleDeleteOneOffSession = (sessionId: string) => {
    const session = sessions.find((s) => s.sessionId === sessionId);
    if (session) {
      setDeletingSession(session);
      setShowDeleteConfirmDialog(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingSession) return;

    try {
      await deleteOneOffSession.deleteOneOffSession(deletingSession.sessionId);
      setSnackbar({
        open: true,
        message: `Successfully deleted one-off session "${deletingSession.name}"`,
        variant: 'success',
      });
      setShowDeleteConfirmDialog(false);
      setDeletingSession(null);
      // Refresh the data
      if (handleSuccessRefetch) {
        handleSuccessRefetch();
      }
    } catch (error) {
      console.error('Failed to delete one-off session:', error);
      setSnackbar({
        open: true,
        message: `Failed to delete one-off session "${deletingSession.name}"`,
        variant: 'error',
      });
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmDialog(false);
    setDeletingSession(null);
  };

  const handleEditSubmit = async (sessionData: any) => {
    if (!editingSession) return;

    try {
      await editOneOffSession.editOneOffSession(sessionData);
      setSnackbar({
        open: true,
        message: `Successfully updated one-off session "${editingSession.name}"`,
        variant: 'success',
      });
      setShowEditDialog(false);
      setEditingSession(null);
      // Refresh the data
      if (handleSuccessRefetch) {
        handleSuccessRefetch();
      }
    } catch (error) {
      console.error('Failed to edit one-off session:', error);
      setSnackbar({
        open: true,
        message: `Failed to update one-off session "${editingSession.name}"`,
        variant: 'error',
      });
    }
  };

  const handleEditDialogClose = () => {
    setShowEditDialog(false);
    setEditingSession(null);
  };

  return (
    <>
      {viewMaxVolunteersPrompt && (
        <MaxVolunteersPrompt onCancel={() => setViewMaxVolunteersPrompt(false)} onOK={onOk} onTick={onTick} />
      )}
      <LinearProgressOverlay isLoading={loading} />
      <Box
        marginTop='1rem'
        paddingBottom='6rem'
        style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
        minHeight={'auto'}
        position='relative'
      >
        <Box position='sticky' top={0} bgcolor='white' zIndex={'100'} paddingTop={'1em'}>
          <RosterHeader
            rosterSessions={sessionsData}
            activityName={activityName}
            rosterName={rosterName}
            activityDate={activityDate}
            activityStatus={activityStatus}
            activityId={activityId}
            setShowCancelActivityDialog={setShowCancelActivityDialog}
            handleSuccessRefetch={handleSuccessRefetch}
            hasPublishedRoster={hasPublishedRoster}
            currentRosterState={currentRosterState}
            isRosterCancelled={isRosterCancelled}
            isRosterCompleted={isRosterCompleted}
            onAddVoluteer={() => setOpenAddVolunteersDialog(true)}
            setSortedVolunteerIdsByEnrolmentDate={setSortedVolunteerIdsByEnrolmentDate}
            onShowRosterNotes={onShowRosterNotes}
            hasRosterNotes={Boolean(rosterNotes)}
            hasRosterName={Boolean(rosterName)}
            componentRef={componentRef}
            publishedRoster={publishedRoster}
            onToggleRestricted={onToggleRestricted}
            onFillRoster={handleFillRoster}
          />
        </Box>
        <div style={{ display: 'none' }}>
          <div ref={componentRef}>
            <RosterTableForPrint
              key={`${activityId}-${activityDate}`}
              activityName={activityName}
              orgLogo={orgLogo}
              onChangeAvailability={onChangeAvailability}
              sessions={sessionsData}
              draftRosters={draftRoster || []}
              onClickRosterStatus={onClickRosterStatus}
              isRosterCancelled={isRosterCancelled}
              isRosterCompleted={isRosterCompleted}
              onSubmitReport={onSubmitReport}
              publishedRoster={publishedRoster}
              activityId={activityId}
              activityDate={activityDate}
              sortBy={sortVolsRowBy}
              sortedVolunteerIdsByEnrolmentDate={sortedVolunteerIdsByEnrolmentDate}
              sessionNotes={sessionNotes}
              onChangeSessionNote={onChangeSessionNote}
              onEditOneOffSession={handleEditOneOffSession}
              onDeleteOneOffSession={handleDeleteOneOffSession}
            />
          </div>
        </div>
        <RosterTable
          key={`${activityId}-${activityDate}`}
          onChangeAvailability={onChangeAvailability}
          sessions={slicedSessions}
          draftRosters={draftRoster || []}
          onClickRosterStatus={onClickRosterStatus}
          isRosterCancelled={isRosterCancelled}
          isRosterCompleted={isRosterCompleted}
          onSubmitReport={onSubmitReport}
          publishedRoster={publishedRoster}
          activityId={activityId}
          activityDate={activityDate}
          sortBy={sortVolsRowBy}
          sortedVolunteerIdsByEnrolmentDate={sortedVolunteerIdsByEnrolmentDate}
          sessionNotes={sessionNotes}
          onChangeSessionNote={onChangeSessionNote}
          onEditOneOffSession={handleEditOneOffSession}
          onDeleteOneOffSession={handleDeleteOneOffSession}
          isPendingApproval={isPendingApproval}
          allowIndividualShiftTimes={allowIndividualShiftTimes}
          onSetSessionTime={onSetSessionTime}
          onRemoveRostering={onRemoveRostering}
          applicantHolds={applicantHolds}
        />
      </Box>
      {showRosterNotes && (
        <RosterNotesDialog
          open
          edit={isEditRosterNote}
          rosterNotes={rosterNotes}
          rosterName={rosterName}
          onCancel={() => setShowRosterNotes(false)}
          onSave={handleSaveRosterNotes}
        />
      )}
      {!isRosterCancelled && (
        <RosterActionsFooter
          open={currentRosterState !== 'published' || !!isPendingApproval}
          hasUnsavedChanges={currentRosterState === 'not-saved'}
          hasPublishedRoster={hasPublishedRoster}
          changedVolunteers={updatedRosterings}
          onPublishRosterClick={() => handlePublishRoster(true)}
          onSaveDraftClick={() => handlePublishRoster(false)}
          onDiscardChangesClick={handleDiscardChanges}
          isSubmitting={false}
          suppressDrafts={false}
          isPendingApproval={isPendingApproval}
          canApproveRoster={canApproveRoster}
          onApproveRosterClick={onApproveRosterClick}
          onRejectRosterClick={onRejectRosterClick}
        />
      )}
      {settingRoster && (
        <ConflictingDialog
          onCancel={onCancelConflict}
          onContinue={onContinueConflict}
          conflictRosters={conflictingRoster}
        />
      )}
      {showEditDialog && editingSession && (
        <EditOneOffSessionDialog
          open={showEditDialog}
          onClose={handleEditDialogClose}
          onSubmit={handleEditSubmit}
          session={editingSession}
          activityDate={DateTime.fromISO(activityDate)}
          activityId={activityId}
        />
      )}
      <ConfirmationDialog
        open={showDeleteConfirmDialog}
        title='Delete One-Off Session'
        body={
          deletingSession
            ? `Are you sure you want to delete the one-off session "${deletingSession.name}"? This action cannot be undone.`
            : ''
        }
        closeActionText='Cancel'
        confirmActionText='Delete'
        handleCloseClick={handleCancelDelete}
        handleConfirmClick={handleConfirmDelete}
        disableConfirm={deleteOneOffSession.isLoading}
      />
    </>
  );
};
